import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logout } from '../slice/userSlice';

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds
const CHECK_INTERVAL = 15 * 1000; // Check every 15 seconds
const ACTIVITY_THROTTLE = 5 * 1000; // Throttle activity updates to once per 5 seconds
const LAST_ACTIVITY_KEY = 'admin_last_activity';

export const useAutoLogout = (isActive = true) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lastRecordedRef = useRef(0);
  const isLoggedOutRef = useRef(false);

  const performLogout = useCallback(() => {
    if (isLoggedOutRef.current) return;
    isLoggedOutRef.current = true;

    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('userToken');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    sessionStorage.removeItem('userToken');
    sessionStorage.clear();

    try {
      dispatch(logout());
    } catch (e) {
      console.error('Error dispatching logout:', e);
    }

    toast.info('Session expired due to 1 hour of inactivity. Please sign in again.');
    navigate('/signin', { replace: true });
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!isActive) return;

    // Initialize last activity timestamp if not present
    const existingActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    const now = Date.now();
    if (!existingActivity) {
      localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
      lastRecordedRef.current = now;
    } else {
      const parsed = parseInt(existingActivity, 10);
      if (now - parsed >= INACTIVITY_TIMEOUT) {
        performLogout();
        return;
      }
      lastRecordedRef.current = parsed;
    }

    // Handler to record user activity (throttled)
    const updateActivity = () => {
      const current = Date.now();
      if (current - lastRecordedRef.current >= ACTIVITY_THROTTLE) {
        lastRecordedRef.current = current;
        localStorage.setItem(LAST_ACTIVITY_KEY, current.toString());
      }
    };

    // Periodic check and immediate check on tab focus/visibility
    const checkInactivity = () => {
      const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
      const lastAct = stored ? parseInt(stored, 10) : lastRecordedRef.current;
      if (Date.now() - lastAct >= INACTIVITY_TIMEOUT) {
        performLogout();
      }
    };

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        checkInactivity();
      }
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((event) => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    const intervalId = setInterval(checkInactivity, CHECK_INTERVAL);

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      clearInterval(intervalId);
    };
  }, [isActive, performLogout]);
};

export default useAutoLogout;
